import { useT } from "../../contexts/LanguageContext";
import React from "react";
import { XMarkIcon, CheckBadgeIcon } from "@heroicons/react/24/outline";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU") : "—");
const formatAmount = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n || 0);

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h4 className="text-xs uppercase tracking-wide text-text-secondary mb-2">{title}</h4>
    {children}
  </div>
);

const UserProfileDrawer = ({ profile, onClose }) => {
  const t = useT();
  if (!profile) return null;
  const u = profile.user || {};
  return (
    <div className="h-full w-full lg:w-96 dark-card border-l border-gray-700 overflow-y-auto">
      <header className="flex items-center justify-between p-4 border-b border-gray-700">
        <h3 className="text-white font-semibold">{t("profile.userProfile")}</h3>
        <button onClick={onClose} className="text-text-secondary hover:text-white">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </header>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-16 h-16 rounded-full bg-accent-grad flex items-center justify-center text-white text-2xl font-bold">
            {u.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <div className="text-white font-semibold flex items-center gap-2">
              {u.name || "—"}
              {u.is_verified && (
                <CheckBadgeIcon className="w-4 h-4 text-blue-400" title={t("profile.verified")} />
              )}
            </div>
            <div className="text-sm text-text-secondary">{u.phone_number || u.email}</div>
          </div>
        </div>

        <Section title={t("profile.title")}>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">Email</span>
              <span className="text-white">{u.email || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("profile.type")}</span>
              <span className="text-white">{u.user_type || "—"}</span>
            </div>
            {u.dealer_role && (
              <div className="flex justify-between">
                <span className="text-text-secondary">{t("profile.dealerRole")}</span>
                <span className="text-white">{u.dealer_role}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("profile.balance")}</span>
              <span className="text-white">{formatAmount(u.balance)} ₸</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("profile.rating")}</span>
              <span className="text-white">{u.rating ?? "—"}</span>
            </div>
            {/* Нақты онлайн (сокет) — бұрын тек «аккаунт белсенді» ✅ көрсетілетін,
                сондықтан бәрі «онлайн» болып көрінетін. */}
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("profile.online")}</span>
              <span className={u.is_online ? "text-green-400" : "text-text-secondary"}>
                {u.is_online
                  ? `● ${t("profile.onlineNow")}`
                  : u.last_online
                    ? `${t("profile.lastOnline")} ${formatDate(u.last_online)}`
                    : "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("profile.active")}</span>
              <span className="text-white">{u.is_active ? t("profile.accountActive") : t("profile.accountDisabled")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("profile.lastActive")}</span>
              <span className="text-white">{formatDate(u.last_active_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("profile.registered")}</span>
              <span className="text-white">{formatDate(u.created_at)}</span>
            </div>
          </div>
        </Section>

        {profile.kyc && (
          <Section title="KYC">
            <div className="dark-card p-3 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">{t("profile.kycStatus")}</span>
                <span className="text-white">{profile.kyc.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">{t("profile.docType")}</span>
                <span className="text-white">{profile.kyc.doc_type}</span>
              </div>
              {profile.kyc.doc_url && (
                <a
                  href={profile.kyc.doc_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 text-sm block mt-1"
                >
                  {t("profile.document")} →
                </a>
              )}
            </div>
          </Section>
        )}

        {profile.announcements?.length > 0 && (
          <Section title={`${t("profile.announcements")} (${profile.announcements.length})`}>
            <div className="space-y-1.5">
              {profile.announcements.map((a) => (
                <div key={a.id} className="dark-card p-2 rounded text-sm">
                  <div className="text-white truncate">{a.title}</div>
                  <div className="flex justify-between text-xs text-text-secondary">
                    <span>{a.status}</span>
                    <span>{formatAmount(a.price)} ₸</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {profile.orders?.length > 0 && (
          <Section title={`Тапсырыстар (${profile.orders.length})`}>
            <div className="space-y-1.5">
              {profile.orders.map((o) => (
                <div key={o.id} className="dark-card p-2 rounded text-sm">
                  <div className="flex justify-between">
                    <span className="text-white">#{o.id}</span>
                    <span className="text-white">{formatAmount(o.total_amount)} ₸</span>
                  </div>
                  <div className="flex justify-between text-xs text-text-secondary">
                    <span>{o.status}</span>
                    <span>{formatDate(o.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {profile.past_chats?.length > 0 && (
          <Section title={t("profile.oldChats")}>
            <div className="space-y-1.5">
              {profile.past_chats.map((c) => (
                <div key={c.chat_id} className="dark-card p-2 rounded text-xs">
                  <div className="flex justify-between text-white">
                    <span>#{c.chat_id}</span>
                    <span>{c.closed_at ? "closed" : "open"}</span>
                  </div>
                  {c.resolution_note && (
                    <div className="text-text-secondary mt-1 truncate">
                      {c.resolution_note}
                    </div>
                  )}
                  <div className="text-text-secondary mt-0.5">
                    {formatDate(c.assigned_at)}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default UserProfileDrawer;
